import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { IBusinessToolSections } from 'constants/businessTool';
import {
  useBusinessPlanFileDelete,
  useBusinessPlanFileUpload,
  useBusinessPlanFileUploadComplete,
} from 'services/businessPlanFiles/hooks';
import { useUpdateBusinessPlanChapter } from 'services/businessToolForm/hooks/useUpdateChapter';
import { useImageUpload } from 'services/files/hooks/useImageUpload';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessToolChapterSelector, getSetBusinessPlanChapter } from 'store/useBusinessToolStore/selectors';

import usePromiseStatus from 'hooks/usePromiseStatus';

import { IImageFile, IImageWithoutFile } from 'interfaces/files';

import FileDropzone from 'components/_basic/FileDropzone';
import { formatImgUploaderError } from 'components/BusinessToolForm/utils/helpers';

import { getValueByPath, updateObjectByPath } from 'helpers/businessTool';
import { compressImage } from 'helpers/compressImage';
import { getVariable } from 'helpers/environment.utils';

import { IFormImageUploadComponentProps } from './types';

const MAX_FILE_SIZE = 5;
const FILE_EXTENSION = { 'image/*': [] };

export const FormImageUploadComponent: React.FC<IFormImageUploadComponentProps> = ({
  name,
  maxSize = MAX_FILE_SIZE,
  acceptedFormats = FILE_EXTENSION,
  ...restProps
}) => {
  const setBusinessPlanChapter = useBusinessToolStore(getSetBusinessPlanChapter);
  const getBusinessToolChapter = useBusinessToolStore(getBusinessToolChapterSelector);

  // TODO better to pass chapterName via prop
  const [chapterName, chapterPath] = name.split(/\.(.+)/) as [IBusinessToolSections, string];

  const chapterContent = getBusinessToolChapter(chapterName);
  const businessPlanImageUpload = useBusinessPlanFileUpload();

  const files = getValueByPath(chapterContent, chapterPath) as IImageWithoutFile[];
  const [activeIndexFile, setActiveIndexFile] = useState<number | null>(null);

  const { getValues, setValue } = useFormContext();
  const { track, isPending, error } = usePromiseStatus();

  const { id: businessPlanId = '' } = useParams();

  const { mutate: updateBusinessPlanChapter, isPending: isPendingUpdateBusinessPlanChapter } =
    useUpdateBusinessPlanChapter({
      onSuccess: (data) => {
        const content = data.chapter?.content;
        if (content) {
          const parsedContent = JSON.parse(content);
          setBusinessPlanChapter(chapterName, parsedContent);
        }
      },
    });

  const onSuccessDeleting = () => {
    if (Number.isInteger(activeIndexFile)) {
      const currentFiles: IImageFile[] = getValues(name);
      const newFiles = currentFiles.filter((_, i) => i !== activeIndexFile);
      const newContent = updateObjectByPath(chapterContent, chapterPath, newFiles);
      const body = { chapterName, content: JSON.stringify(newContent) };

      updateBusinessPlanChapter({ id: businessPlanId, body });
      setBusinessPlanChapter(chapterName, newContent);
    }
  };

  const { mutate: businessPlanFileDelete, isPending: isPendingBusinessPlanFileDelete } = useBusinessPlanFileDelete({
    onSuccess: onSuccessDeleting,
  });

  const isDeleting = isPendingBusinessPlanFileDelete || isPendingUpdateBusinessPlanChapter;

  const businessPlanImageUploadComplete = useBusinessPlanFileUploadComplete();
  const imageUploadMutation = useImageUpload();

  const handleFileUpload = async (acceptedFiles: File[]) => {
    try {
      const files: IImageWithoutFile[] = await track(
        Promise.all(
          acceptedFiles.map(async (file) => {
            const compressedFile = await compressImage(file);

            const { fileUuid, url } = await businessPlanImageUpload.mutateAsync({
              id: businessPlanId,
              filePayload: {
                filename: compressedFile.name,
                contentType: compressedFile.type,
                size: compressedFile.size,
              },
            });

            await imageUploadMutation.mutateAsync({ file: compressedFile, url });
            const uploadedFile = await businessPlanImageUploadComplete.mutateAsync({ id: businessPlanId, fileUuid });

            return {
              id: uploadedFile.fileUuid,
              url: `${getVariable('REACT_APP_ENV_API_SHORT_LINK')}/${uploadedFile.url}`,
              filename: compressedFile.name,
            };
          }),
        ),
      );

      setValue(name, files);
    } catch (error) {
      return;
    }
  };

  const handleFileDelete = (index: number) => {
    const currentFiles: IImageFile[] = getValues(name);
    const fileToDelete = currentFiles[index];

    setActiveIndexFile(index);
    businessPlanFileDelete({
      id: businessPlanId,
      fileUuid: fileToDelete.id,
    });
  };

  return (
    <FileDropzone
      {...restProps}
      files={files}
      name={name}
      maxSize={maxSize}
      acceptedFormats={acceptedFormats}
      errorText={formatImgUploaderError(error)}
      isLoading={isPending || isDeleting}
      onFileUpload={handleFileUpload}
      onDelete={handleFileDelete}
    />
  );
};
